// Warm narrator built on the browser's SpeechSynthesis API. Prefers a
// UK / AU / NZ-flavoured English voice so the naturalist field-notes tone
// carries through. Zero network cost.

const isBrowser = typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined";

const PREFERRED_NAMES = [
  // High-quality voices when available
  "Google UK English Female",
  "Karen", // macOS AU
  "Serena", // macOS UK
  "Kate", // macOS UK
  "Daniel", // macOS UK
  "Google UK English Male",
  "Microsoft Libby Online",
  "Microsoft Sonia Online",
  "Microsoft George Online",
];

const PREFERRED_LOCALES = ["en-NZ", "en-AU", "en-GB", "en-IE", "en-US"];

let cachedVoice = null;
let voicesLoaded = false;

const loadVoices = () => {
  if (!isBrowser) return [];
  const list = window.speechSynthesis.getVoices();
  if (list && list.length) voicesLoaded = true;
  return list || [];
};

const pickVoice = () => {
  if (cachedVoice) return cachedVoice;
  const voices = loadVoices();
  if (!voices.length) return null;
  // Try preferred names first
  for (const name of PREFERRED_NAMES) {
    const v = voices.find((vc) => vc.name === name);
    if (v) {
      cachedVoice = v;
      return v;
    }
  }
  // Then preferred locales, favouring female-sounding voices when possible
  for (const loc of PREFERRED_LOCALES) {
    const localeMatches = voices.filter((v) => v.lang === loc);
    if (localeMatches.length) {
      const female = localeMatches.find((v) =>
        /female|libby|sonia|serena|karen|kate|amelie|siri.*female|natural/i.test(v.name)
      );
      cachedVoice = female || localeMatches[0];
      return cachedVoice;
    }
  }
  cachedVoice = voices[0];
  return cachedVoice;
};

// Fire once when voices become available (Chromium loads them async)
if (isBrowser && typeof window.speechSynthesis.onvoiceschanged !== "undefined") {
  window.speechSynthesis.onvoiceschanged = () => {
    voicesLoaded = true;
    cachedVoice = null; // re-pick with the full list
  };
}

let currentUtter = null;

export const speak = (text, { onEnd, onStart } = {}) => {
  if (!isBrowser || !text) return false;
  stopSpeaking();
  const voice = pickVoice();
  const utter = new SpeechSynthesisUtterance(text);
  if (voice) utter.voice = voice;
  utter.lang = voice?.lang || "en-GB";
  utter.rate = 0.95; // slow, contemplative
  utter.pitch = 1.0;
  utter.volume = 1.0;
  utter.onstart = () => onStart && onStart();
  utter.onend = () => {
    currentUtter = null;
    onEnd && onEnd();
  };
  utter.onerror = () => {
    currentUtter = null;
    onEnd && onEnd();
  };
  currentUtter = utter;
  window.speechSynthesis.speak(utter);
  return true;
};

export const stopSpeaking = () => {
  if (!isBrowser) return;
  window.speechSynthesis.cancel();
  currentUtter = null;
};

export const narratorAvailable = () => isBrowser;
